import React, { useEffect, useState } from 'react'
import {
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box, CircularProgress,
  Tooltip
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { AlertDialog, IconButtonWithTooltip, EditActions, formatDate, useUsUrlDescTypeLuts } from 'components'
import { type LeadershipUrl, type PersonUrl, type UsUrlRoleDescType } from 'services'
import { URL_TYPES } from 'utils'
import { SOCIAL_URL_WARNING_TITLE, socialUrlUnderWebsiteWarning, validateUrl } from 'helpers/urls/urlValidation'
import { isEmailUs } from 'helpers'
import { useSessionContext } from 'session'

interface UrlCardItemProps<T extends PersonUrl | LeadershipUrl> {
  originalUrl: T
  onSave: (url: T, onComplete: () => void) => void
  onDelete: (url: T, onComplete: () => void) => void
  startDisabled?: boolean
  showButtons?: boolean
  roleIds?: number[]
  updateOriginal?: (url: T) => void // New prop
}

export const UrlCardItem = <T extends PersonUrl | LeadershipUrl> ({
  originalUrl,
  onSave,
  onDelete,
  startDisabled = true,
  showButtons = true,
  roleIds,
  updateOriginal
}: UrlCardItemProps<T>): React.ReactElement => {
  const [disabled, setDisabled] = useState(startDisabled)
  const [loading, setLoading] = useState(false)
  const [url, setUrl] = useState(originalUrl)
  const [selectedTypeName, setSelectedTypeName] = useState<string | undefined>(() => {
    if (originalUrl.typeId == null) return undefined

    for (const [key, value] of Object.entries(URL_TYPES)) {
      if (value.id === originalUrl.typeId) return key.toLowerCase()
    }

    return undefined
  })
  const [urlErrorMessage, setUrlErrorMessage] = useState<string | null>(null)
  const [isError, setIsError] = useState(false)
  const [isSaveDisabled, setIsSaveDisabled] = useState(true)
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)
  const [socialWarning, setSocialWarning] = useState('')
  const { lutValues: descTypeValues } = useUsUrlDescTypeLuts()
  const { openNewTab } = useSessionContext()

  const socialMediaTypes: Record<string, string> = {
    facebook: 'facebook',
    linkedin: 'linkedin',
    twitter: 'twitter',
    youtube: 'youtube',
    instagram: 'instagram'
  }

  const determineValidationType = (): string | undefined => {
    const descTypeName = url.descId != null ? getDescTypeNameById(Number(url.descId)) : ''
    const lowerDesc = descTypeName.toLowerCase()
    const matchedSocialType = Object.keys(socialMediaTypes).find(key => lowerDesc.includes(key))

    if (matchedSocialType !== undefined) {
      return socialMediaTypes[matchedSocialType]
    }

    if (lowerDesc.includes('email webform')) {
      return 'emailWebform'
    }

    return selectedTypeName
  }

  const roleMap = new Map<number, { id: number, desc: string, lutOrder: number }>()
  Array.from(descTypeValues.descTypes.values()).forEach(descType => {
    const shouldIncludeRole = roleIds === null || roleIds === undefined || roleIds.length === 0 || roleIds.includes(descType.role.id)

    if (shouldIncludeRole) {
      if (!roleMap.has(descType.role.id)) {
        roleMap.set(descType.role.id, {
          id: descType.role.id,
          desc: descType.role.desc,
          lutOrder: descType.lutOrder ?? 999
        })
      }
    }
  })
  const availableRoles = Array.from(roleMap.values()).sort((a, b) => a.lutOrder - b.lutOrder)

  const filteredDescTypes = Array.from(descTypeValues.descTypes.values()).filter((descType: UsUrlRoleDescType) => {
    const roleMatches = selectedRoleId !== null
      ? descType.role.id === selectedRoleId
      : availableRoles.length === 1
        ? descType.role.id === availableRoles[0].id
        : availableRoles.some(role => role.id === descType.role.id)

    const typeMatches = url.typeId != null ? descType.type.id === url.typeId : true

    return roleMatches && typeMatches
  })

  const storedDescType = url.descId != null ? descTypeValues.descTypes.get(url.descId) : undefined

  const selectableDescTypes = storedDescType != null && !filteredDescTypes.some(descType => descType.id === url.descId)
    ? [...filteredDescTypes, storedDescType]
    : filteredDescTypes

  useEffect(() => {
    if (url.descId != null) {
      const currentDescType = Array.from(descTypeValues.descTypes.values()).find(desc => desc.id === url.descId)
      if (currentDescType?.role !== null && currentDescType?.role !== undefined && selectedRoleId !== currentDescType.role.id) {
        setSelectedRoleId(currentDescType.role.id)
      }
    } else if (availableRoles.length === 1 && selectedRoleId !== availableRoles[0].id) {
      setSelectedRoleId(availableRoles[0].id)
    }
  }, [url.descId, descTypeValues.descTypes, selectedRoleId, availableRoles])

  useEffect(() => {
    if (storedDescType == null && filteredDescTypes.length > 0) {
      const firstAvailableOption = filteredDescTypes[0].id
      setUrl(prevState => ({ ...prevState, descId: firstAvailableOption }))
      updateOriginal?.({ ...url, descId: firstAvailableOption })
    }
  }, [filteredDescTypes, storedDescType, url.descId])

  useEffect(() => {
    setUrl(originalUrl)
    setLoading(false)
    setDisabled(startDisabled)
  }, [originalUrl])

  useEffect(() => {
    if (!showButtons) {
      setDisabled(startDisabled)
    }
  }, [startDisabled])

  useEffect(() => {
    if (url.value !== '' && selectedTypeName !== '') {
      validateUrlField(url.value, selectedTypeName)
    }
  }, [selectedTypeName])

  useEffect(() => {
    if (url.value !== '') {
      validateUrlField(url.value)
    }
  }, [url.descId, storedDescType])

  const validateUrlField = (urlValue: string, typeName?: string): void => {
    setIsSaveDisabled(true)
    setIsError(false)

    if (urlValue === '') {
      setUrlErrorMessage('Link (email or website) is required')
      setIsError(true)
      return
    }

    const effectiveType = typeName !== undefined ? typeName : determineValidationType()

    if (effectiveType === undefined || effectiveType === '') {
      setUrlErrorMessage('URL type is required')
      setIsError(true)
      return
    }

    const isUrlValid = validateUrl(urlValue, effectiveType)

    if (!isUrlValid.isValid) {
      setUrlErrorMessage(isUrlValid.message)
      setIsError(true)
    } else {
      setUrlErrorMessage(null)
      setIsError(false)
      setIsSaveDisabled(storedDescType == null)
    }
  }

  const handleDelete = (url: T): void => {
    setLoading(true)
    onDelete(url, () => {
      setLoading(false)
    })
  }

  const saveUrl = (url: T): void => {
    setLoading(true)
    onSave(url, () => {
      setLoading(false)
    })
  }

  const handleSave = (url: T): void => {
    const descType = url.descId != null ? descTypeValues.descTypes.get(url.descId) : undefined
    const warning = descType != null ? socialUrlUnderWebsiteWarning(url.value, descType.desc, descType.contentType) : undefined
    if (warning !== undefined) {
      setSocialWarning(warning)
      return
    }
    saveUrl(url)
  }

  const handleOpenNewPageClick = (): void => {
    openNewTab(url.value, false)
  }

  const isUrlValid = (): boolean => {
    return !isError && url.value.trim() !== '' && url.descId != null && url.descId > 0
  }

  const handleUrlChange = (newValue: string): void => {
    validateUrlField(newValue)

    const newUrl = { ...url, value: newValue }
    setUrl(newUrl)
    updateOriginal?.(newUrl)
  }

  const handleTypeChange = (newValue: number): void => {
    validateUrlField(url.value)

    const currentDescType = url.descId != null ? descTypeValues.descTypes.get(url.descId) : undefined
    const descStillMatchesType = currentDescType != null && currentDescType.type.id === newValue

    const newUrl = { ...url, typeId: Number(newValue), descId: descStillMatchesType ? url.descId : undefined }
    setUrl(newUrl)
    updateOriginal?.(newUrl)

    const nameOfType = Object.entries(URL_TYPES).find(
      ([, value]) => value.id === newValue
    )

    setSelectedTypeName((nameOfType != null) ? nameOfType[0].toLowerCase() : undefined)
  }

  const handleRoleChange = (newRoleId: number): void => {
    setSelectedRoleId(newRoleId)
    // Clear the current descId when role changes, user will need to select a new desc
    const newUrl = { ...url, descId: undefined }
    setUrl(newUrl)
    updateOriginal?.(newUrl)
  }

  const handleDescChange = (newValue: number): void => {
    const newUrl = { ...url, descId: Number(newValue) }
    setUrl(newUrl)
    updateOriginal?.(newUrl)
  }

  const getDescTypeNameById = (descId: number): string => {
    return descTypeValues.descTypes.get(descId)?.desc ?? ''
  }

  const renderOpenLinkButton = (additionalDisabled: boolean = false): React.ReactElement => {
    return (
      <IconButtonWithTooltip
        label="Open Website"
        color="secondary"
        onClick={handleOpenNewPageClick}
        icon={<OpenInNewIcon />}
        disabled={additionalDisabled || (isEmailUs(url) && determineValidationType() !== 'emailWebform') || !isUrlValid()}
      />
    )
  }

  return (
    <Grid container key={url.id} spacing={1} wrap="nowrap" sx={{ mb: 2 }}>
      <Grid item xs={5}>
        <TextField
          label={url.value?.length !== 0
            ? `Last Updated: ${formatDate(url.lastUpdated)}`
            : 'Please enter the new Link (email or website)'
          }
          variant="outlined"
          value={url.value}
          disabled={disabled}
          onChange={(e) => { handleUrlChange(e.target.value) }}
          helperText={urlErrorMessage}
          error={isError}
          fullWidth
        />
      </Grid>
      <Grid item xs={1.5}>
        <FormControl fullWidth>
          <InputLabel>URL Type</InputLabel>
          <Select
            value={url.typeId != null ? url.typeId : ''}
            disabled={disabled}
            onChange={(e) => { handleTypeChange(Number(e.target.value)) }}
            label="URL Type"
          >
            {Object.entries(URL_TYPES).map(([key, value]) => (
              <MenuItem key={value.id} value={value.id}>
                {key.charAt(0) + key.slice(1).toLowerCase().replace('_', ' ')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {availableRoles.length > 1 && (
        <Grid item xs={2}>
          <FormControl fullWidth>
            <InputLabel>Role</InputLabel>
            <Select
              value={selectedRoleId != null ? selectedRoleId : ''}
              disabled={disabled}
              onChange={(e) => { handleRoleChange(Number(e.target.value)) }}
              label="Role"
            >
              {availableRoles.map((role) => (
                <MenuItem key={role.id} value={role.id}>
                  {role.desc}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      <Grid item xs sx={{ minWidth: 0 }}>
        <Tooltip title={url.descId != null ? getDescTypeNameById(url.descId) : ''}>
          <FormControl fullWidth>
            <InputLabel>Description</InputLabel>
            <Select
              value={url.descId != null ? url.descId : ''}
              disabled={disabled || (availableRoles.length > 1 && selectedRoleId === null)}
              onChange={(e) => { handleDescChange(Number(e.target.value)) }}
              label="Description"
            >
              {selectableDescTypes
                .sort((a, b) => (a.lutOrder ?? 999) - (b.lutOrder ?? 999))
                .map((descType) => (
                  <MenuItem key={descType.id} value={descType.id}>
                    {descType.desc}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Tooltip>
      </Grid>
      {showButtons
        ? <Grid item xs="auto">
            <Box sx={{ display: 'flex', gap: 1 }}>
              {loading
                ? (
                <CircularProgress />
                  )
                : (
                <>
                  <EditActions
                    entityName="URL"
                    editing={!disabled}
                    saveDisabled={isSaveDisabled}
                    onEdit={() => { setDisabled(false) }}
                    onCancel={() => { setUrl(originalUrl); setDisabled(true) }}
                    onSave={() => { handleSave(url) }}
                  />
                  {disabled && (
                    <IconButtonWithTooltip
                      label="Delete URL"
                      color="secondary"
                      onClick={() => { handleDelete(originalUrl) }}
                      icon={<DeleteIcon />}
                    />
                  )}
                  {renderOpenLinkButton()}
                </>
                  )}
            </Box>
          </Grid>
        : <Grid item xs="auto" sx={{ display: 'flex' }}>
            <IconButtonWithTooltip
              label="Delete URL"
              color="secondary"
              disabled={disabled}
              onClick={() => { handleDelete(originalUrl) }}
              icon={<DeleteIcon />}
            />
            {renderOpenLinkButton(disabled)}
          </Grid>
      }
      <AlertDialog
        title={SOCIAL_URL_WARNING_TITLE}
        contentText={socialWarning}
        nameButtonCancel='Cancel'
        nameButtonConfirm='Save anyway'
        onButtonClick={() => { if (!loading) saveUrl(url) }}
        onClose={() => { setSocialWarning('') }}
        open={socialWarning !== ''}
        containedButton
      />
    </Grid>
  )
}
